import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Calendar,
  Award,
} from 'lucide-react';
import { Card } from '../components/ui';
import { useServerStore } from '../store';
import { api } from '../lib/api';

interface AnalyticsData {
  totalPredictions: number;
  successRate: number;
  averageConfidence: number;
}

export function Analytics() {
  const { currentServer } = useServerStore();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!currentServer) return;

    const loadAnalytics = async () => {
      try {
        const response = await api.analytics.getServer(currentServer.id);
        if (response.success && response.data) {
          setAnalytics(response.data);
        }
      } catch (error) {
        console.error('Failed to load analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalytics();
  }, [currentServer]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-foretell-accent" />
      </div>
    );
  }

  return (
    <div className="animate-in">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-foretell-accent/20 flex items-center justify-center text-foretell-accent">
          <BarChart3 size={20} />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Analytics</h2>
          <p className="text-sm text-slate-400">
            {currentServer?.name || 'Server'} prediction insights
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-foretell-accent/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-2 text-slate-400 mb-2">
              <Target size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">
                Total Predictions
              </span>
            </div>
            <div className="text-4xl font-bold">{analytics?.totalPredictions || 0}</div>
          </div>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-2 text-slate-400 mb-2">
              <TrendingUp size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">
                Success Rate
              </span>
            </div>
            <div className="text-4xl font-bold text-emerald-400">
              {analytics?.successRate.toFixed(1) || 0}%
            </div>
          </div>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-2 text-slate-400 mb-2">
              <Award size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">
                Avg Confidence
              </span>
            </div>
            <div className="text-4xl font-bold">
              {analytics?.averageConfidence.toFixed(0) || 0}%
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-foretell-accent" />
            Monthly Activity
          </h3>
          <div className="h-48 flex items-end justify-between gap-2">
            {[...Array(6)].map((_, i) => {
              const height = Math.random() * 100 + 20;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                  <div
                    className="w-full bg-foretell-accent/20 rounded-t-lg transition-all hover:bg-foretell-accent/40"
                    style={{ height: `${height}%` }}
                  />
                  <span className="text-[10px] text-slate-400">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'][i]}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Users size={18} className="text-foretell-accent" />
            Top Predictors
          </h3>
          <div className="space-y-3">
            {[
              { name: 'Alice', predictions: 45, accuracy: 78 },
              { name: 'Bob', predictions: 38, accuracy: 72 },
              { name: 'Charlie', predictions: 32, accuracy: 85 },
              { name: 'Diana', predictions: 28, accuracy: 68 },
              { name: 'Eve', predictions: 24, accuracy: 75 },
            ].map((user, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-foretell-accent/20 flex items-center justify-center text-xs font-bold text-foretell-accent">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold">{user.name}</div>
                  <div className="text-[10px] text-slate-400">
                    {user.predictions} predictions · {user.accuracy}% accuracy
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="mt-6">
        <h3 className="font-bold mb-4">Confidence vs Accuracy</h3>
        <p className="text-sm text-slate-400 mb-4">
          How well do confidence levels predict actual outcomes?
        </p>
        <div className="grid grid-cols-5 gap-4">
          {[
            { range: '0-20%', accuracy: 15 },
            { range: '21-40%', accuracy: 32 },
            { range: '41-60%', accuracy: 55 },
            { range: '61-80%', accuracy: 68 },
            { range: '81-100%', accuracy: 82 },
          ].map((item, i) => (
            <div key={i} className="text-center">
              <div className="h-24 flex items-end justify-center mb-2">
                <div
                  className="w-full max-w-[40px] bg-gradient-to-t from-foretell-accent to-purple-500 rounded-t-lg"
                  style={{ height: `${item.accuracy}%` }}
                />
              </div>
              <div className="text-[10px] text-slate-400">{item.range}</div>
              <div className="text-xs font-bold">{item.accuracy}%</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
